import { useEffect, useState } from "react";

import { App } from "antd";
import { useTranslation } from "react-i18next";

import {
  NotificationTypeCategories,
  NotificationTypes,
} from "@/shared/utils/constants";
import {
  useGetNotificationSettingsForUserQuery,
  usePatchNotificationSettingsMutation,
} from "@/redux/service";
import BaseButton from "@/components_v2/Button/BaseButton";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import BaseSwitch from "@/components_v2/FormFields/Switch/BaseSwitch";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import {
  NotificationSettingsMap,
  NotificationSettingState,
  SettingCategory,
  SettingTypeEntry,
} from "@/features/users/settings/types";

import "../styles.css";

const NotificationSettings = () => {
  const { t } = useTranslation();
  const { notification } = App.useApp();

  const {
    data: notificationSettingsData,
    isSuccess: isNotificationSettingsSuccess,
  } = useGetNotificationSettingsForUserQuery();
  const [patchNotificationSettings] = usePatchNotificationSettingsMutation();

  const [notificationSettings, setNotificationSettings] =
    useState<NotificationSettingsMap | null>(null);

  const notificationTypeEntries = Object.values(NotificationTypes).filter(
    (entry): entry is SettingTypeEntry => typeof entry !== "function",
  );

  useEffect(() => {
    if (isNotificationSettingsSuccess) {
      const settings = notificationSettingsData.reduce<NotificationSettingsMap>(
        (acc, i) => {
          acc[i.type] = {
            id: i.id,
            enabled: i.enabled,
          };
          return acc;
        },
        {},
      );
      setNotificationSettings(settings);
    }
  }, [notificationSettingsData, isNotificationSettingsSuccess]);

  const handleChangeForNotificationType = (
    notificationType: string,
    value: boolean,
  ) => {
    const setting = notificationSettings?.[notificationType];
    if (!setting) return;
    patchNotificationSettings({ id: setting.id, data: { enabled: value } })
      .unwrap()
      .then(() => {
        notification.success({
          message: t("users.labels.notificationsSettingsUpdated"),
        });
      });
  };

  const handleDisableAll = (category: SettingCategory) => {
    if (!notificationSettings) return;
    const promises = notificationTypeEntries
      .filter((type) => type.category === category.category)
      .map((type) => notificationSettings[type.value])
      .filter(
        (setting): setting is NotificationSettingState => !!setting?.enabled,
      )
      .map((setting) =>
        patchNotificationSettings({
          id: setting.id,
          data: { enabled: false },
        }),
      );

    Promise.all(promises).then(() => {
      notification.success({
        message: t("users.labels.notificationsSettingsUpdated"),
      });
    });
  };

  if (!notificationSettings) return <BaseSpinner />;

  return (
    <div className="page-layout">
      {Object.values(NotificationTypeCategories).map((category) => {
        const orderedEntriesForCategory = notificationTypeEntries
          .filter((type) => type.category === category.category)
          .filter((type) => notificationSettings[type.value]);
        const areAllDisabled = orderedEntriesForCategory.every(
          (entry) => !notificationSettings[entry.value]?.enabled,
        );
        return (
          <div
            className="user-settings-card flex flex-col gap-300"
            key={category.category}
          >
            <div className="flex items-center justify-between gap-200">
              <h3 className="header-m-medium">{t(category.labelKey)}</h3>
              <BaseButton
                variant={BUTTON_VARIANTS.LINK}
                size={BUTTON_SIZES.SMALL}
                disabled={areAllDisabled}
                onClick={() => handleDisableAll(category)}
              >
                {t("users.labels.disableAll")}
              </BaseButton>
            </div>

            <div>
              {orderedEntriesForCategory.map((entry) => (
                <div className="user-settings-toggle-row" key={entry.value}>
                  <span className="body-m-regular">{t(entry.labelKey)}</span>
                  <BaseSwitch
                    checked={notificationSettings[entry.value]?.enabled}
                    onChange={(e) =>
                      handleChangeForNotificationType(entry.value, e)
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default NotificationSettings;
